import type { CalCredentials } from "@/models/provider/CalCredentials";
import { createLogger } from "../logger";
import { time } from "../time";
import { getBaseUrl } from "../utils";
import { CAL_TERMINAL_REFRESH_401_CODES } from "./refresh-errors";

const logger = createLogger("lib/cal/tokens");

interface CalRefreshResponse {
  accessToken?: string;
  error?: string;
  code?: string;
}

interface RefreshCredentialsOptions {
  currentAccessToken?: string;
  forceRefresh?: boolean;
}

function isTerminalRefreshAuthFailureResponse({
  status,
  responseBody,
}: {
  status: number;
  responseBody: CalRefreshResponse | undefined;
}): boolean {
  if (status !== 401) {
    return false;
  }

  const code = responseBody?.code;
  if (typeof code === "string") {
    return CAL_TERMINAL_REFRESH_401_CODES.has(code);
  }

  const error = responseBody?.error;
  if (typeof error !== "string") {
    // Unknown 401 shape: keep current token and allow caller retry paths.
    return false;
  }

  const normalized = error.toLowerCase();
  return (
    normalized.includes("invalid access token") ||
    normalized.includes("credentials owner missing cal user id") ||
    normalized.includes("missing or invalid authorization header")
  );
}

/**
 * Returns a valid Cal.com access token, refreshing credentials if the token is
 * within 10 minutes of expiring. Uses the public refresh endpoint which also
 * persists updated credentials server-side.
 */
export async function refreshCredentialsIfExpired(
  credentials: CalCredentials,
  options: RefreshCredentialsOptions = {},
): Promise<string> {
  let token = options.currentAccessToken ?? credentials.accessToken;
  const expiresAt = credentials.accessTokenExpiresAt;
  const needsRefresh = time(expiresAt).diff(time(), "minute") < 10;
  const shouldRefresh = options.forceRefresh || needsRefresh;
  const refreshUrl =
    typeof window === "undefined" ? `${getBaseUrl()}/api/cal/refresh` : "/api/cal/refresh";

  if (!token) {
    return token;
  }

  if (shouldRefresh) {
    try {
      const response = await fetch(refreshUrl, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      const responseBody = (await response.json().catch(() => undefined)) as
        | CalRefreshResponse
        | undefined;

      if (response.ok) {
        if (responseBody?.accessToken) {
          token = responseBody.accessToken;
        }
      } else if (
        isTerminalRefreshAuthFailureResponse({
          status: response.status,
          responseBody,
        })
      ) {
        logger.error("Cal refresh rejected access token", {
          expiresAt,
          minutesUntilExpiry: time(expiresAt).diff(time(), "minute"),
          refreshStatusCode: response.status,
          refreshErrorCode: responseBody?.code,
          refreshError: responseBody?.error,
        });
        return "";
      } else {
        logger.warn("Cal refresh request returned non-ok response", {
          expiresAt,
          minutesUntilExpiry: time(expiresAt).diff(time(), "minute"),
          refreshStatusCode: response.status,
          refreshError: responseBody?.error,
        });
      }
    } catch (error) {
      logger.warn("Failed to refresh Cal.com credentials", {
        error: error instanceof Error ? error.message : String(error),
        expiresAt,
        minutesUntilExpiry: time(expiresAt).diff(time(), "minute"),
      });
    }
  }

  return token;
}
